import {
  Button,
  Combobox,
  FIELD_BASE,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  Skeleton,
  Modal,
  cn,
} from "@dd/ui";
import { useQuery } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import {
  Armchair,
  CalendarDays,
  CalendarPlus,
  ChartColumnIncreasing,
  Check,
  ChevronDown,
  LayoutDashboard,
  ListTodo,
  LogOut,
  Users,
  Boxes,
  Building2,
  Database,
  DollarSign,
  Star,
  Stethoscope,
  Menu,
  MapPin,
  PackagePlus,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  X,
  Settings,
  Search,
  UserPlus,
  GitPullRequest,
  ClipboardList,
  Pill,
  BriefcaseMedical,
  Megaphone,
  Clock3,
  CircleHelp,
  ShieldCheck,
  Timer,
} from "lucide-react";
import { RondaLogo, RondaMark } from "../ronda-logo.tsx";
import { OrganizationLogo } from "../organization-logo.tsx";
import { api } from "../../lib/api.ts";
import { keys, queryClient } from "../../lib/query.ts";
import { useEffect, useState, type ReactNode } from "react";
import { fetchSessionUser, getUser, signOut } from "../../lib/auth.ts";
import { useUiStore } from "../../store/ui.ts";
import { Notifications } from "./notifications.tsx";
import { CommandPalette } from "./command-palette.tsx";
import { StepUpDialog } from "../step-up-dialog.tsx";
import { PlatformAssistant } from "../platform-assistant.tsx";
import {
  subscriptionGrantsAccess,
  subscriptionRequiresPayment,
  trialDaysRemaining as remainingTrialDays,
} from "../../lib/subscription-access.ts";

/** El ancho de los dos controles de la barra superior: sede y buscador. */
const TOPBAR_FIELD_WIDTH = "w-48 shrink-0";
const DEMO_URL =
  "https://calendly.com/anastasia-unbroker/30min?&embed_type=Inline&month=2026-09";
const SUPPORT_URL =
  "https://wa.me/5491130310188?text=Hola%20Ronda%2C%20necesito%20ayuda%20con%20la%20plataforma.";

/**
 * El menú, en grupos.
 *
 * Cada grupo responde a un contexto de trabajo. Mantener cinco opciones o
 * menos por bloque evita que la barra lateral se lea como una lista plana de
 * módulos sin jerarquía.
 */
const NAV_GROUPS = [
  {
    label: "Atención",
    items: [
      { to: "/", label: "Tablero", icon: LayoutDashboard },
      { to: "/appointments", label: "Turnos", icon: CalendarDays },
      { to: "/waiting-room", label: "Sala de espera", icon: Armchair },
      { to: "/patients", label: "Pacientes", icon: Users },
      { to: "/tasks", label: "Pendientes", icon: ListTodo },
    ],
  },
  {
    label: "Clínica",
    items: [
      { to: "/referrals", label: "Derivaciones", icon: GitPullRequest },
      { to: "/clinical-forms", label: "Formularios", icon: ClipboardList },
      { to: "/prescriptions", label: "Recetas", icon: Pill },
      // Junto a Inventario, que es lo que los tratamientos consumen, y no
      // adentro: estuvo como pestaña de esa pantalla y nadie lo encontró.
      { to: "/treatments", label: "Tratamientos", icon: BriefcaseMedical },
      { to: "/inventory", label: "Inventario", icon: Boxes },
    ],
  },
  {
    label: "Negocio",
    items: [
      { to: "/metrics", label: "Métricas", icon: ChartColumnIncreasing },
      { to: "/professionals", label: "Profesionales", icon: Stethoscope },
      { to: "/crm", label: "Base / CRM", icon: Database },
      { to: "/finances", label: "Finanzas", icon: DollarSign },
      { to: "/surveys", label: "Encuestas", icon: Star },
      { to: "/marketing", label: "Marketing", icon: Megaphone },
    ],
  },
] as const;

/*
 * No hay forma de bajar la aplicación de escritorio desde acá.
 *
 * El instalador todavía no está firmado ni verificado por Apple ni por
 * Microsoft, así que al abrirlo el sistema operativo avisa que viene de un
 * origen desconocido. Ofrecerlo en la barra superior y en el menú de la cuenta
 * ponía a una médica a decidir si saltear esa advertencia, que es exactamente
 * lo que no queremos enseñarle a hacer.
 *
 * `apps/desktop` sigue existiendo y se sigue construyendo y probando: lo que se
 * sacó es la invitación a instalarlo, hasta que el paquete esté firmado.
 */

type NavPath = (typeof NAV_GROUPS)[number]["items"][number]["to"];

/** Tooltip de navegación: aparece a la derecha y funciona con mouse y teclado. */
function CollapsedTooltip({
  label,
  disabled = false,
  children,
}: {
  label: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  if (disabled) return children;
  return (
    <div className="group/tooltip relative">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-[calc(100%+0.65rem)] top-1/2 z-[120] hidden -translate-y-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground opacity-0 shadow-lg transition-opacity group-hover/tooltip:block group-hover/tooltip:opacity-100 group-focus-within/tooltip:block group-focus-within/tooltip:opacity-100"
      >
        {label}
      </span>
    </div>
  );
}

export function AppShell() {
  const navigate = useNavigate();
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const activeLocationId = useUiStore((s) => s.activeLocationId);
  const setActiveLocation = useUiStore((s) => s.setActiveLocation);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  const [commandOpen, setCommandOpen] = useState(false);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  // La copia local pinta el encabezado en el primer cuadro —el nombre no puede
  // aparecer un instante después— y la consulta lo corrige con lo que dice el
  // servidor si cambió.
  const { data: user } = useQuery({
    queryKey: keys.sessionUser,
    queryFn: fetchSessionUser,
    initialData: getUser,
    // Sin esto no se pide nada: `initialData` entra con la hora actual, el
    // `staleTime` de 30 s del cliente la da por fresca y el encabezado se queda
    // con lo que había guardado el navegador —que es justamente el problema.
    initialDataUpdatedAt: 0,
  });
  const { data: organizationStatus, isPending } = useQuery({
    queryKey: keys.organization,
    queryFn: api.organization.status,
  });
  const organization = organizationStatus?.organization;
  const {
    data: subscriptionStatus,
    isPending: subscriptionPending,
    isError: subscriptionError,
    refetch: refetchSubscription,
  } = useQuery({
    queryKey: keys.subscription,
    queryFn: api.subscription.status,
    enabled: Boolean(organization),
    refetchInterval: 60_000,
  });
  const subscriptionActive = subscriptionGrantsAccess(subscriptionStatus);
  const trialEndsAt = subscriptionStatus?.trial.endsAt ?? null;
  const trialActive = Boolean(
    !subscriptionActive && trialEndsAt && trialEndsAt > Date.now(),
  );
  const trialDaysRemaining = remainingTrialDays(trialEndsAt);
  const trialBannerTone =
    trialDaysRemaining <= 1
      ? "border-danger/25 bg-danger/10 text-danger"
      : trialDaysRemaining <= 3
        ? "border-warning/30 bg-warning/10 text-warning"
        : "border-primary/20 bg-primary/5 text-primary";
  const trialMessage =
    trialDaysRemaining === 1
      ? "Tu prueba gratuita termina mañana"
      : `Te quedan ${trialDaysRemaining} días de prueba gratuita`;
  const paywalled = subscriptionRequiresPayment(
    subscriptionStatus,
    subscriptionRequired,
  );
  const billingPage = pathname === "/organization/billing";
  const { data: locations = [], isSuccess: locationsLoaded } = useQuery({
    queryKey: keys.locations,
    queryFn: api.locations.list,
    enabled: Boolean(organization),
  });
  // Una sede eliminada o que dejó de estar dentro del alcance del usuario no
  // puede quedar persistida como un filtro invisible. En cuanto conocemos la
  // lista válida, volvemos al consolidado.
  useEffect(() => {
    if (
      locationsLoaded &&
      activeLocationId &&
      !locations.some((location) => location.id === activeLocationId)
    ) {
      setActiveLocation(null);
    }
  }, [activeLocationId, locations, locationsLoaded, setActiveLocation]);
  useEffect(() => {
    if (!sidebarOpen) return;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSidebarOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [sidebarOpen, setSidebarOpen]);
  useEffect(() => {
    const requireSubscription = () => setSubscriptionRequired(true);
    window.addEventListener("dd:subscription-required", requireSubscription);
    return () =>
      window.removeEventListener(
        "dd:subscription-required",
        requireSubscription,
      );
  }, []);
  useEffect(() => setSubscriptionRequired(false), [organization?.id]);
  useEffect(() => {
    const key = `ronda-trial-banner-dismissed:${organization?.id ?? "none"}`;
    setTrialBannerDismissed(sessionStorage.getItem(key) === "1");
  }, [organization?.id]);
  // Finanzas no se atenúa para quien no puede verla: no está en el menú.
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (item.to === "/finances")
        return (organization?.permissions ?? []).includes("finances.read");
      if (item.to === "/professionals")
        return (organization?.permissions ?? []).includes("team.read");
      return true;
    }),
  })).filter((group) => group.items.length > 0);
  function isRouteActive(to: NavPath): boolean {
    return to === "/"
      ? pathname === "/"
      : pathname === to || pathname.startsWith(`${to}/`);
  }

  function handleSignOut() {
    queryClient.clear();
    signOut();
    void navigate({ to: "/login" });
  }

  return (
    <div className="flex min-h-full">
      <StepUpDialog />
      <aside
        data-tour="navigation"
        className={cn(
          // `relative z-10`: por encima de la capa de luz, que va en z-0.
          "desktop-app-sidebar sticky top-0 z-20 hidden h-screen shrink-0 flex-col border-r border-border bg-card transition-[width] duration-200 md:flex",
          sidebarCollapsed ? "w-16" : "w-[184px]",
        )}
      >
        {/* El nombre del producto, en grande: es lo primero que se lee al
            abrir el panel, y a 14 píxeles competía con los renglones del menú
            en lugar de encabezarlos. */}
        <div className="flex h-14 items-center border-b border-border px-2">
          {/* La versión va apoyada en la misma línea de base que el nombre y en
              el verde de `success`: sirve para contestar "¿qué tengo yo
              abierto?" cuando algo no cuadra, y a 11 píxeles el color no le
              pelea el lugar a la marca. Es un token y no un verde suelto,
              así que el modo oscuro lo aclara solo. */}
          <Link
            to="/"
            aria-label="Ir al inicio"
            className={cn(
              "flex h-10 w-full cursor-pointer items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              sidebarCollapsed ? "justify-center" : "px-2",
            )}
          >
            {sidebarCollapsed ? (
              <RondaMark className="h-7 w-7" />
            ) : (
              <RondaLogo className="h-5 w-[3.85rem]" />
            )}
          </Link>
        </div>

        <div
          className={cn(
            "sidebar-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain py-3",
            sidebarCollapsed ? "px-2" : "px-2.5",
          )}
        >
          {isPending ? (
            <Skeleton className="mb-4 h-12" />
          ) : (
            organization && (
              <div className="mb-4">
                {renderOrganizationSwitcher(sidebarCollapsed)}
              </div>
            )
          )}
          <nav className="space-y-4">
            {groups.map((group) => (
              <div key={group.label}>
                {!sidebarCollapsed && (
                  <p className="mb-1.5 px-2.5 text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                    {group.label}
                  </p>
                )}
                <SidebarMenu>
                  {group.items.map(({ to, label, icon: Icon }) => (
                    <SidebarMenuItem key={to}>
                      <CollapsedTooltip
                        label={label}
                        disabled={!sidebarCollapsed}
                      >
                        <SidebarMenuButton
                          asChild
                          isActive={isRouteActive(to)}
                          className={
                            sidebarCollapsed
                              ? "justify-center px-0 hover:translate-x-0"
                              : undefined
                          }
                        >
                          <Link
                            to={to}
                            aria-label={sidebarCollapsed ? label : undefined}
                          >
                            <Icon />
                            {!sidebarCollapsed && <span>{label}</span>}
                          </Link>
                        </SidebarMenuButton>
                      </CollapsedTooltip>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </div>
            ))}
          </nav>
          <div className="mt-auto border-t border-border pt-3">
            <CollapsedTooltip
              label={sidebarCollapsed ? "Expandir menú" : "Colapsar menú"}
              disabled={!sidebarCollapsed}
            >
              <button
                type="button"
                aria-label={
                  sidebarCollapsed
                    ? "Expandir menú lateral"
                    : "Colapsar menú lateral"
                }
                className={cn(
                  "flex h-9 w-full items-center gap-2 rounded-xl px-2.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  sidebarCollapsed && "justify-center px-0",
                )}
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              >
                {sidebarCollapsed ? (
                  <PanelLeftOpen className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
                {!sidebarCollapsed && <span>Colapsar menú</span>}
              </button>
            </CollapsedTooltip>
          </div>
        </div>
      </aside>

      <main className="relative z-10 min-w-0 flex-1">
        <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-card/95 px-4 backdrop-blur md:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Abrir menú"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <p className="max-w-[45%] truncate text-sm font-semibold">
            {organization?.name ?? "Ronda"}
          </p>
          {/* Sin la descarga del escritorio: lo que ofrece son instaladores de
              macOS y Windows, y un teléfono no tiene dónde ponerlos. Ocupaba un
              lugar en la barra más apretada del producto para terminar en un
              archivo que quien lo bajó no puede abrir. Sigue en la barra de
              escritorio, que es desde donde se instala. */}
          {/* Tampoco el "+" de acciones rápidas. Con más de una sede este
              renglón llega a cinco controles sobre 320 px y el nombre de la
              organización queda recortado a la mitad. Lo que ofrecía no se
              pierde: "Nuevo turno", "Nuevo paciente" y "Cargar insumo" tienen
              cada uno su botón propio en su pantalla, y el Tablero abre con la
              tarjeta de acciones rápidas. Sigue en la barra de escritorio,
              donde el espacio no está en disputa. */}
          <div className="flex items-center gap-1">
            {renderLocationSwitcher(true)}
            <Notifications />
            <UserDropdown />
          </div>
        </div>

        {sidebarOpen && (
          <div
            className="ronda-drawer-overlay fixed inset-0 z-50 md:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Navegación principal"
          >
            <button
              type="button"
              className="absolute inset-0 bg-foreground/40"
              aria-label="Cerrar menú"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="ronda-drawer-panel relative flex h-full w-[min(86vw,320px)] flex-col overflow-y-auto bg-card p-4 shadow-xl">
              <div className="mb-5 flex items-center justify-between">
                <Link
                  to="/"
                  aria-label="Ir al inicio"
                  onClick={() => setSidebarOpen(false)}
                  className="flex cursor-pointer items-center gap-2.5 rounded-lg p-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <RondaLogo className="h-7 w-[5.4rem]" />
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Cerrar menú"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              {organization && (
                // Abre lo mismo que en escritorio: con "Organización" fuera
                // del menú, esta tarjeta es la puerta a la configuración, y
                // en el teléfono era un cartel que no se podía tocar.
                <div className="mb-5">{renderOrganizationSwitcher()}</div>
              )}
              <nav className="space-y-5">
                {groups.map((group) => (
                  <div key={group.label}>
                    <p className="mb-2 px-3 text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                      {group.label}
                    </p>
                    <SidebarMenu>
                      {group.items.map(({ to, label, icon: Icon }) => (
                        <SidebarMenuItem key={to}>
                          <SidebarMenuButton
                            asChild
                            isActive={isRouteActive(to)}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <Link to={to}>
                              <Icon />
                              <span>{label}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </div>
                ))}
              </nav>
            </aside>
          </div>
        )}

        <header className="hidden h-14 items-center gap-2 border-b border-border bg-card/95 px-5 backdrop-blur md:flex">
          {locations.length > 1 && (
            <div className={TOPBAR_FIELD_WIDTH}>{renderLocationSwitcher()}</div>
          )}
          {/* El buscador se viste con `FIELD_BASE`, el mismo molde que usa el
              selector de sede: alto, radio, borde y fondo salen de un solo
              lugar. Tenía los suyos a mano —un píxel más bajo, otro borde— y
              dos controles casi iguales pegados uno al lado del otro se leen
              como un error de alineación, no como dos cosas distintas. */}
          <button
            data-tour="search"
            type="button"
            className={cn(
              FIELD_BASE,
              TOPBAR_FIELD_WIDTH,
              "flex h-10 items-center gap-2 text-muted-foreground transition-colors hover:text-foreground",
            )}
            onClick={() => setCommandOpen(true)}
            aria-label="Buscar en Ronda"
          >
            <Search className="h-4 w-4 shrink-0" />
            <span>Buscar</span>
            <kbd className="ml-auto text-[10px]">⌘ K</kbd>
          </button>
          <div className="ml-auto flex items-center gap-1">
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 w-32 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CalendarDays className="h-4 w-4" />
              Pedir demo
            </a>
            <span data-tour="create">
              <QuickCreateDropdown />
            </span>
            <Notifications />
            <UserDropdown />
          </div>
        </header>
        {trialActive && !trialBannerDismissed && (
          <div
            className={`flex min-h-10 items-center justify-between gap-3 border-b px-4 py-2 text-xs ${trialBannerTone}`}
          >
            <div className="flex min-w-0 items-center gap-2">
              <Clock3 className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="truncate font-medium">{trialMessage}</span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 bg-background/80 px-2.5 text-xs"
                onClick={() => void navigate({ to: "/organization/billing" })}
              >
                Ver planes
              </Button>
              <button
                type="button"
                className="grid h-7 w-7 place-items-center rounded-md hover:bg-foreground/10"
                aria-label="Cerrar aviso de prueba gratuita"
                onClick={() => {
                  sessionStorage.setItem(
                    `ronda-trial-banner-dismissed:${organization?.id ?? "none"}`,
                    "1",
                  );
                  setTrialBannerDismissed(true);
                }}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
        {/*
          El espacio de abajo está reservado para el asistente, que flota fijo
          en esa esquina sobre todas las pantallas. Sin él, cualquier acción
          que cierre una página quedaba literalmente debajo de la burbuja:
          "Archivar ficha", al final de la ficha del paciente y alineada a la
          derecha, caía entera dentro de sus 186 píxeles y no había forma de
          hacerle clic.

          Va acá y no en esa pantalla porque el problema no es de esa pantalla:
          lo mismo le pasa a cualquier página larga que termine con un botón a
          la derecha. Y va como `pb` suelto en vez de `p`, porque el atajo se
          repite en cada punto de quiebre y volvería a pisar el margen inferior
          en `sm` y `lg`.
        */}
        {/* `overflow-x-clip` y no `hidden`: recorta lo que se sale de costado
            sin convertir el contenedor en una zona de desplazamiento, así que
            las barras pegajosas de adentro siguen pegándose a la pantalla. Lo
            que se salía eran los globos de ayuda, que se centran en su ícono y
            con dos tarjetas por renglón asoman por el borde; bastaba eso para
            que toda la página corriera de costado. Las tablas que sí necesitan
            desplazarse tienen el suyo propio y siguen funcionando. */}
        <div className="mx-auto max-w-[1280px] overflow-x-clip px-4 pb-24 pt-4 sm:px-6 sm:pt-6 lg:px-7 lg:pt-7">
          {organization && subscriptionPending ? (
            <div aria-label="Cargando acceso de la organización">
              <Skeleton className="h-10 w-56" />
              <Skeleton className="mt-5 h-72 w-full" />
            </div>
          ) : organization && subscriptionError ? (
            <div
              role="alert"
              className="mx-auto max-w-xl rounded-xl border border-danger/25 bg-card p-6 text-center"
            >
              <p className="font-semibold">
                No pudimos verificar el acceso de la organización
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Reintentá antes de continuar para evitar trabajar con un estado
                de suscripción desactualizado.
              </p>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => void refetchSubscription()}
              >
                Reintentar
              </Button>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </main>
      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
      <PlatformAssistant />
      <Modal
        open={paywalled && !billingPage}
        onClose={() => undefined}
        dismissible={false}
        title="Tu período de prueba finalizó"
        description={
          subscriptionStatus?.subscription
            ? "La suscripción de esta organización ya no está activa."
            : "Elegí un plan para seguir trabajando en Ronda."
        }
        size="md"
      >
        <div className="grid gap-5">
          <div className="flex justify-center">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary">
              <Timer className="h-6 w-6" aria-hidden="true" />
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-xl border border-primary/15 bg-primary/5 p-4">
            <ShieldCheck
              className="mt-0.5 h-5 w-5 shrink-0 text-primary"
              aria-hidden="true"
            />
            <div>
              <p className="text-sm font-medium">Tus datos siguen seguros</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                La información de la organización permanece guardada. Al activar
                un plan vas a poder continuar desde donde quedaste.
              </p>
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold text-foreground">
              Elegí el plan que mejor se adapte a tu equipo
            </p>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              {[
                ["Consultorio", "1 profesional"],
                ["Clínica", "3 profesionales"],
                ["Centro", "10 profesionales"],
              ].map(([name, detail]) => (
                <div key={name} className="rounded-lg border p-2.5">
                  <span className="block font-medium">{name}</span>
                  <span className="mt-0.5 block text-muted-foreground">
                    {detail}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <Button
            className="w-full"
            onClick={() => void navigate({ to: "/organization/billing" })}
          >
            Ver planes y precios
          </Button>
        </div>
      </Modal>
    </div>
  );

  // Render helper, intentionally not a nested React component. A nested
  // component gets a new identity on every AppShell render (including every
  // route change), which used to unmount and refetch the organization logo.
  function renderOrganizationSwitcher(compact = false) {
    if (!organization) return null;
    const roleLabel: Record<typeof organization.role, string> = {
      owner: "Titular",
      admin: "Administración",
      clinician: "Profesional clínico",
      reception: "Recepción",
      billing: "Facturación",
      auditor: "Auditoría",
      member: "Profesional clínico",
    };

    const switcher = (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex min-h-11 w-full items-center rounded-md border border-border bg-card text-left outline-none transition-colors hover:bg-background focus-visible:ring-2 focus-visible:ring-ring",
              compact ? "justify-center px-1" : "gap-2.5 px-2.5",
            )}
            aria-label="Cambiar organización"
          >
            <OrganizationLogo
              name={organization.name}
              organizationId={organization.id}
              hasLogo={organization.hasLogo}
              version={organization.logoUpdatedAt}
              className="h-7 w-7 shrink-0 rounded-md text-[10px]"
            />
            {!compact && (
              <>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-semibold">
                    {organization.name}
                  </span>
                  <span className="block text-[11px] text-muted-foreground">
                    {roleLabel[organization.role]}
                  </span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel>Tus organizaciones</DropdownMenuLabel>
          {organizationStatus?.organizations?.map((item) => (
            <DropdownMenuItem
              key={item.id}
              onSelect={() => {
                if (item.id === organization.id) {
                  void navigate({ to: "/organization" });
                  return;
                }
                setActiveLocation(null);
                localStorage.setItem("dd-organization-id", item.id);
                queryClient.clear();
                location.assign("/");
              }}
            >
              <OrganizationLogo
                name={item.name}
                organizationId={item.id}
                hasLogo={item.hasLogo}
                version={item.logoUpdatedAt}
                className="h-7 w-7 shrink-0 rounded-md text-[10px]"
              />
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              {item.id === organization.id && (
                <span
                  className="flex shrink-0 items-center gap-1.5"
                  aria-label="Organización actual; abrir configuración"
                >
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <Check className="h-4 w-4 text-primary" />
                </span>
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() =>
              void navigate({
                to: "/organizations",
                search: { action: "new" },
              })
            }
          >
            <Plus className="h-4 w-4" /> Crear nueva organización
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => void navigate({ to: "/organizations" })}
          >
            <Building2 className="h-4 w-4" /> Administrar organizaciones
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
    return compact ? (
      <CollapsedTooltip label={organization.name}>{switcher}</CollapsedTooltip>
    ) : (
      switcher
    );
  }

  function QuickCreateDropdown({ compact = false }: { compact?: boolean }) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size={compact ? "icon" : "sm"}
            aria-label="Crear nuevo"
            className={compact ? "shadow-none" : "w-28 justify-center"}
          >
            <Plus className="h-4 w-4" />
            {!compact && "Crear"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Acción rápida</DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() =>
              void navigate({
                to: "/appointments",
                search: { action: "new" },
              })
            }
          >
            <CalendarPlus className="h-4 w-4" /> Nuevo turno
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              void navigate({ to: "/patients", search: { action: "new" } })
            }
          >
            <UserPlus className="h-4 w-4" /> Nuevo paciente
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              void navigate({ to: "/inventory", search: { action: "new" } })
            }
          >
            <PackagePlus className="h-4 w-4" /> Cargar insumo
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  /**
   * El botón de la cuenta.
   *
   * `compact` deja sólo el círculo, para la barra del teléfono donde el nombre
   * no entra. El rol ya no se muestra acá —ni en el botón ni en el menú—: la
   * pantalla de organización lo dice junto a los permisos, que es donde se
   * puede hacer algo con ese dato.
   */
  function UserDropdown() {
    const initials = (user?.name ?? "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center rounded-full text-left ring-offset-background transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-95"
            aria-label="Abrir menú de usuario"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
              {initials || "?"}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>
            <span className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {initials || "?"}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-foreground">
                  {user?.name}
                </span>
                <span className="block truncate text-[11px] font-normal text-muted-foreground">
                  {user?.email}
                </span>
              </span>
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => void navigate({ to: "/settings/profile" })}
          >
            <Users className="h-4 w-4" /> Perfil personal
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => void navigate({ to: "/settings/security" })}
          >
            <Settings className="h-4 w-4" /> Seguridad
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => void navigate({ to: "/organization" })}
          >
            <Building2 className="h-4 w-4" /> Organización
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              globalThis.open(SUPPORT_URL, "_blank", "noopener,noreferrer")
            }
          >
            <CircleHelp className="h-4 w-4" /> Ayuda
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-danger focus:text-danger"
            onSelect={handleSignOut}
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  /**
   * La sede es un filtro global y por eso abre la barra superior, antes de las
   * acciones que operan dentro de ese contexto. Con una sola sede no hay
   * decisión que tomar; con dos o más aparece en escritorio y como pin compacto
   * en el teléfono.
   */
  function renderLocationSwitcher(compact = false) {
    if (locations.length <= 1) return null;
    const options = [{ id: "", name: "Todas las sedes" }, ...locations];

    if (compact) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Cambiar sede">
              <MapPin className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Sede visible</DropdownMenuLabel>
            {options.map((location) => (
              <DropdownMenuItem
                key={location.id || "all"}
                onSelect={() => setActiveLocation(location.id || null)}
              >
                <MapPin />
                <span className="flex-1">{location.name}</span>
                {(activeLocationId ?? "") === location.id && <Check />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <Combobox
        id="navbar-sede"
        value={activeLocationId ?? ""}
        aria-label="Sede que se está mirando"
        options={options.map((location) => ({
          value: location.id,
          label: location.name,
        }))}
        searchable={locations.length > 8}
        searchPlaceholder="Buscar sede…"
        onChange={(value) => setActiveLocation(value || null)}
      />
    );
  }
}
